const DIALOG_INSTALL_KEY = Symbol.for(
    'rickymau.operationalAccessibilityInstall',
);
const dialogStacks = new WeakMap();
const lastFocusedElements = new WeakMap();
const pendingFocusRestorations = new WeakMap();
const FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableElements(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        canReceiveFocus,
    );
}

function canReceiveFocus(element) {
    const structurallyFocusable = Boolean(
        element?.isConnected !== false &&
            !element?.disabled &&
            !element?.inert &&
            !element?.matches?.(':disabled') &&
            !element?.closest?.('[inert]') &&
            typeof element?.focus === 'function',
    );

    if (!structurallyFocusable) {
        return false;
    }

    if (
        typeof element.getClientRects === 'function' &&
        element.getClientRects().length === 0
    ) {
        return false;
    }

    const styles =
        element.ownerDocument?.defaultView?.getComputedStyle?.(element);

    return styles?.display !== 'none' && styles?.visibility !== 'hidden';
}

function backgroundBranches(root, body) {
    const branches = [];
    const seen = new Set();
    let branch = root;

    while (branch?.parentElement) {
        const parent = branch.parentElement;
        for (const sibling of parent.children ?? []) {
            if (sibling !== branch && !seen.has(sibling)) {
                seen.add(sibling);
                branches.push(sibling);
            }
        }

        if (parent === body) {
            break;
        }

        branch = parent;
    }

    return branches;
}

function dialogStack(documentTarget) {
    let stack = dialogStacks.get(documentTarget);
    if (!stack) {
        stack = [];
        dialogStacks.set(documentTarget, stack);
    }

    return stack;
}

function activeDialog(documentTarget) {
    return dialogStacks.get(documentTarget)?.at(-1) ?? null;
}

function hasDialog(documentTarget) {
    return (dialogStacks.get(documentTarget)?.length ?? 0) > 0;
}

function scheduleFocusRestoration(
    documentTarget,
    restoration,
    attempts = 3,
    deferred = false,
) {
    if (pendingFocusRestorations.get(documentTarget) !== restoration) {
        return;
    }

    const restore = () => {
        if (
            pendingFocusRestorations.get(documentTarget) !== restoration ||
            hasDialog(documentTarget)
        ) {
            return;
        }

        const replacement = restoration.openerId
            ? documentTarget.getElementById?.(restoration.openerId)
            : null;
        const fallback =
            typeof restoration.fallbackFocus === 'string'
                ? documentTarget.querySelector?.(restoration.fallbackFocus)
                : restoration.fallbackFocus;
        const focusTargets = restoration.preferFallbackFocus
            ? [fallback, restoration.opener, replacement]
            : [restoration.opener, replacement, fallback];
        for (const focusTarget of focusTargets) {
            if (!canReceiveFocus(focusTarget)) {
                continue;
            }

            focusTarget.focus({ preventScroll: true });
            if (documentTarget.activeElement === focusTarget) {
                pendingFocusRestorations.delete(documentTarget);

                return;
            }
        }

        if (attempts > 1) {
            scheduleFocusRestoration(
                documentTarget,
                restoration,
                attempts - 1,
                true,
            );
        }
    };
    const schedule = documentTarget.defaultView?.requestAnimationFrame?.bind(
        documentTarget.defaultView,
    );
    if (deferred && schedule) {
        schedule(restore);
    } else {
        restore();
    }
}

function resumeFocusRestoration(documentTarget) {
    const restoration = pendingFocusRestorations.get(documentTarget);
    if (restoration) {
        scheduleFocusRestoration(documentTarget, restoration);
    }
}

function createDialogLifecycle({
    canRequestClose = () => true,
    documentTarget = globalThis.document,
    fallbackFocus = null,
    onRequestClose,
    preferFallbackFocus = false,
    root,
} = {}) {
    let state = 'inactive';
    let backgroundState = [];
    let bodyOverflow = '';
    let closeRequested = false;
    let opener = null;
    let openerId = null;
    let restorationFallbackFocus = fallbackFocus;
    let restorationPreferFallbackFocus = preferFallbackFocus;

    function initialFocus() {
        const preferred = root.querySelector(
            '[data-dialog-initial-focus]:not([disabled])',
        );

        return canReceiveFocus(preferred)
            ? preferred
            : (focusableElements(root)[0] ?? root);
    }

    function requestClose(reason = 'programmatic') {
        if (closeRequested || !canRequestClose()) {
            return false;
        }

        closeRequested = true;
        let closeResult;
        try {
            closeResult = onRequestClose?.(reason);
        } catch {
            closeRequested = false;

            return false;
        }

        Promise.resolve(closeResult)
            .catch(() => {
                if (state !== 'inactive') {
                    closeRequested = false;
                }
            })
            .finally(() => {
                releaseDisconnectedDialog(documentTarget);
                resumeFocusRestoration(documentTarget);
            });

        return true;
    }

    function handleKeydown(event) {
        if (state !== 'active') {
            return;
        }

        if (event.key === 'Escape') {
            if (requestClose('escape')) {
                event.preventDefault();
            }

            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusables = focusableElements(root);
        if (focusables.length === 0) {
            event.preventDefault();
            initialFocus().focus();

            return;
        }

        const first = focusables[0];
        const last = focusables.at(-1);
        const current = documentTarget.activeElement;
        const shouldWrapBackward =
            event.shiftKey && (current === first || !root.contains(current));
        const shouldWrapForward =
            !event.shiftKey && (current === last || !root.contains(current));

        if (shouldWrapBackward || shouldWrapForward) {
            event.preventDefault();
            (shouldWrapBackward ? last : first).focus();
        }
    }

    function handleFocusIn(event) {
        if (state === 'active' && !root.contains(event.target)) {
            initialFocus().focus();
        }
    }

    function acquireEnvironment() {
        bodyOverflow = documentTarget.body?.style?.overflow ?? '';
        backgroundState = backgroundBranches(root, documentTarget.body).map(
            (element) => ({
                ariaHidden: element.getAttribute('aria-hidden'),
                element,
                hadAriaHidden: element.hasAttribute('aria-hidden'),
                hadInert: element.hasAttribute('inert'),
                inert: Boolean(element.inert),
            }),
        );

        for (const state of backgroundState) {
            state.element.inert = true;
            state.element.setAttribute('inert', '');
            state.element.setAttribute('aria-hidden', 'true');
        }

        if (documentTarget.body?.style) {
            documentTarget.body.style.overflow = 'hidden';
        }

        documentTarget.addEventListener('keydown', handleKeydown);
        documentTarget.addEventListener('focusin', handleFocusIn);
        state = 'active';
        initialFocus().focus();
    }

    function releaseEnvironment() {
        documentTarget.removeEventListener('keydown', handleKeydown);
        documentTarget.removeEventListener('focusin', handleFocusIn);

        for (const state of backgroundState) {
            state.element.inert = state.inert;
            if (state.hadInert) {
                state.element.setAttribute('inert', '');
            } else {
                state.element.removeAttribute('inert');
            }

            if (state.hadAriaHidden) {
                state.element.setAttribute('aria-hidden', state.ariaHidden);
            } else {
                state.element.removeAttribute('aria-hidden');
            }
        }

        backgroundState = [];
        if (documentTarget.body?.style) {
            documentTarget.body.style.overflow = bodyOverflow;
        }
    }

    function forgetOpenerWithin(container) {
        if (container?.contains?.(opener)) {
            opener = null;
            openerId = null;
        }
    }

    function suspend() {
        if (state !== 'active') {
            return;
        }

        releaseEnvironment();
        state = 'suspended';
    }

    function resume() {
        if (state !== 'suspended' || !api.isConnected()) {
            return false;
        }

        acquireEnvironment();

        return true;
    }

    function activate(
        focusOrigin = {
            current: documentTarget.activeElement,
            remembered: lastFocusedElements.get(documentTarget),
        },
    ) {
        if (state !== 'inactive') {
            return;
        }

        pendingFocusRestorations.delete(documentTarget);
        restorationFallbackFocus = fallbackFocus;
        restorationPreferFallbackFocus = preferFallbackFocus;
        const currentFocus = focusOrigin.current;
        const rememberedFocus = focusOrigin.remembered;
        const currentFocusIsDocument =
            currentFocus === documentTarget.body ||
            currentFocus === documentTarget.documentElement;
        opener =
            root.contains(currentFocus) || currentFocusIsDocument
                ? rememberedFocus?.element
                : currentFocus;
        openerId = opener?.id || rememberedFocus?.id || null;

        const stack = dialogStack(documentTarget);
        const previousDialog = stack.at(-1);
        if (previousDialog && previousDialog !== api) {
            previousDialog.suspend();
        }
        stack.push(api);
        closeRequested = false;
        acquireEnvironment();
    }

    function deactivate({ restoreFocus = true, resumeParent = true } = {}) {
        if (state === 'inactive') {
            return;
        }

        const stack = dialogStacks.get(documentTarget) ?? [];
        const index = stack.indexOf(api);
        const wasTop = index === stack.length - 1;
        if (state === 'active') {
            releaseEnvironment();
        }
        state = 'inactive';
        closeRequested = false;

        if (index !== -1) {
            if (!wasTop) {
                const survivingChild = stack[index + 1];
                survivingChild?.inheritFocusRestoration(focusRestoration());
                for (const descendant of stack.slice(index + 2)) {
                    descendant.forgetOpenerWithin(root);
                }
            }
            stack.splice(index, 1);
        }

        if (stack.length === 0) {
            dialogStacks.delete(documentTarget);
        }

        if (wasTop && resumeParent) {
            let parent = stack.at(-1);
            while (parent && !parent.isConnected()) {
                inheritFocusRestoration(parent.focusRestoration());
                parent.deactivate({ restoreFocus: false, resumeParent: false });
                parent = stack.at(-1);
            }

            if (parent?.resume()) {
                return;
            }
        }

        if (!restoreFocus || hasDialog(documentTarget)) {
            return;
        }

        const restoration = {
            fallbackFocus: restorationFallbackFocus,
            opener,
            openerId,
            preferFallbackFocus: restorationPreferFallbackFocus,
        };
        pendingFocusRestorations.set(documentTarget, restoration);
        scheduleFocusRestoration(documentTarget, restoration);
    }

    function focusRestoration() {
        return {
            fallbackFocus: restorationFallbackFocus,
            opener,
            openerId,
            preferFallbackFocus: restorationPreferFallbackFocus,
        };
    }

    function inheritFocusRestoration(restoration) {
        restorationFallbackFocus = restoration.fallbackFocus;
        opener = restoration.opener;
        openerId = restoration.openerId;
        restorationPreferFallbackFocus = restoration.preferFallbackFocus;
    }

    const api = {
        activate,
        contains: (element) => root.contains(element),
        deactivate,
        focusRestoration,
        forgetOpenerWithin,
        inheritFocusRestoration,
        isActive: () => state === 'active',
        isConnected: () => root.isConnected !== false,
        isEngaged: () => state !== 'inactive',
        requestClose,
        resume,
        suspend,
    };

    return api;
}

function releaseDisconnectedDialog(documentTarget = document) {
    const stack = dialogStacks.get(documentTarget) ?? [];
    for (const dialog of [...stack].reverse()) {
        if (!dialog.isConnected()) {
            dialog.deactivate({ restoreFocus: dialog === stack.at(-1) });
        }
    }
}

function deactivateDialogs(documentTarget, { restoreFocus = false } = {}) {
    const stack = dialogStacks.get(documentTarget) ?? [];
    for (const dialog of [...stack].reverse()) {
        dialog.deactivate({ restoreFocus, resumeParent: false });
    }
    dialogStacks.delete(documentTarget);
}

function createAccessibleDialog({
    autoActivate = true,
    canClose = () => true,
    close,
    fallbackFocus = null,
    preferFallbackFocus = false,
} = {}) {
    let activation = 0;
    let lifecycle = null;

    return {
        activateDialog() {
            const requestedActivation = ++activation;
            const documentTarget = this.$root.ownerDocument;
            // The panel can receive focus before Alpine releases its next tick.
            const focusOrigin = {
                current: documentTarget.activeElement,
                remembered: lastFocusedElements.get(documentTarget),
            };
            const activate = () => {
                if (
                    requestedActivation !== activation ||
                    lifecycle?.isEngaged()
                ) {
                    return;
                }

                lifecycle = createDialogLifecycle({
                    canRequestClose: canClose,
                    documentTarget,
                    fallbackFocus,
                    onRequestClose: close,
                    preferFallbackFocus,
                    root: this.$root,
                });
                lifecycle.activate(focusOrigin);
            };

            if (typeof this.$nextTick === 'function') {
                this.$nextTick(activate);
            } else {
                queueMicrotask(activate);
            }
        },

        deactivateDialog() {
            activation += 1;
            lifecycle?.deactivate();
            lifecycle = null;
        },

        destroy() {
            this.deactivateDialog();
        },

        init() {
            if (autoActivate) {
                this.activateDialog();
            }
        },

        requestDialogClose(reason = 'programmatic') {
            lifecycle?.requestClose(reason);
        },
    };
}

function createConfirmStore({ onError = () => {} } = {}) {
    return {
        busy: false,
        message: '',
        onConfirm: null,
        open: false,
        pending: null,
        sequence: 0,

        async accept() {
            if (this.busy) {
                return this.pending?.catch(() => undefined);
            }

            this.busy = true;
            let operation;
            try {
                operation = Promise.resolve(this.onConfirm?.());
            } catch (error) {
                operation = Promise.reject(error);
            }
            this.pending = operation;

            try {
                await operation;
                if (this.pending === operation) {
                    this.close(true);
                }
            } catch {
                if (this.pending === operation) {
                    this.busy = false;
                    this.pending = null;
                    onError(
                        'No se pudo completar la acción. Inténtalo de nuevo.',
                    );
                }
            }

            return operation.catch(() => undefined);
        },

        close(force = false) {
            if (this.busy && !force) {
                return false;
            }

            this.busy = false;
            this.message = '';
            this.onConfirm = null;
            this.open = false;
            this.pending = null;

            return true;
        },

        openDialog(message, onConfirm) {
            if (this.busy) {
                return false;
            }

            this.message = String(message);
            this.onConfirm = onConfirm;
            this.open = true;
            this.sequence += 1;

            return true;
        },
    };
}

function createToastStore({
    clearTimeoutTarget = (timer) => window.clearTimeout(timer),
    documentTarget = globalThis.document,
    fallbackFocus = null,
    setTimeoutTarget = (callback, delay) => window.setTimeout(callback, delay),
} = {}) {
    const timers = new Map();
    let toastId = 0;

    return {
        items: [],

        dispose() {
            for (const timer of timers.values()) {
                clearTimeoutTarget(timer);
            }

            timers.clear();
            this.items = [];
        },

        push({ message, type = 'info' }) {
            const id = ++toastId;
            const normalizedType = ['error', 'info', 'success'].includes(type)
                ? type
                : 'info';
            this.items.push({
                id,
                message: String(message),
                type: normalizedType,
            });
            timers.set(
                id,
                setTimeoutTarget(() => this.remove(id), 4000),
            );

            return id;
        },

        remove(id, trigger = null) {
            const timer = timers.get(id);
            if (timer !== undefined) {
                clearTimeoutTarget(timer);
                timers.delete(id);
            }

            const index = this.items.findIndex((toast) => toast.id === id);
            const nextId = this.items[index + 1]?.id ?? null;
            const previousId = this.items[index - 1]?.id ?? null;
            const shouldRestoreFocus =
                trigger !== null && documentTarget.activeElement === trigger;
            this.items = this.items.filter((toast) => toast.id !== id);

            if (!shouldRestoreFocus) {
                return;
            }

            const selector = (toastId) =>
                toastId === null
                    ? null
                    : documentTarget.querySelector?.(
                          `[data-toast-close][data-toast-id="${toastId}"]`,
                      );
            const fallback =
                typeof fallbackFocus === 'function'
                    ? fallbackFocus()
                    : typeof fallbackFocus === 'string'
                      ? documentTarget.querySelector?.(fallbackFocus)
                      : fallbackFocus;
            const fallbackTargets = Array.isArray(fallback)
                ? fallback
                : [fallback];
            for (const focusTarget of [
                selector(nextId),
                selector(previousId),
                ...fallbackTargets,
            ]) {
                if (canReceiveFocus(focusTarget)) {
                    focusTarget.focus({ preventScroll: true });
                    if (documentTarget.activeElement !== focusTarget) {
                        continue;
                    }

                    return;
                }
            }
        },
    };
}

function escapeAttribute(value) {
    return String(value).replaceAll('\\', '\\\\').replaceAll('"', '\\"');
}

function createKdsAccessibility({
    afterRender = async () => {
        await new Promise((resolve) => requestAnimationFrame(resolve));
    },
    documentTarget = document,
} = {}) {
    function focusPlan(button) {
        const card = button.closest?.('[data-kds-order-card]');
        const list = card?.closest?.('[data-kds-order-list]');
        const cards = Array.from(
            list?.querySelectorAll?.('[data-kds-order-card]') ?? [],
        );
        const currentIndex = cards.indexOf(card);

        return {
            headingId: list?.dataset?.kdsHeadingId ?? null,
            nextId: cards[currentIndex + 1]?.dataset?.kdsOrderId ?? null,
            previousId: cards[currentIndex - 1]?.dataset?.kdsOrderId ?? null,
        };
    }

    function findCard(orderId, headingId) {
        if (!orderId || !headingId) {
            return null;
        }

        return documentTarget.querySelector?.(
            `[data-kds-order-list][data-kds-heading-id="${escapeAttribute(headingId)}"] [data-kds-order-card][data-kds-order-id="${escapeAttribute(orderId)}"]`,
        );
    }

    function focusFromPlan(plan) {
        const target =
            findCard(plan.nextId, plan.headingId) ??
            findCard(plan.previousId, plan.headingId) ??
            documentTarget.getElementById?.(plan.headingId);
        if (canReceiveFocus(target)) {
            target.focus();

            return target;
        }

        return null;
    }

    return {
        busyAction: null,
        errorMessage: '',
        statusMessage: '',

        isBusy(action) {
            return this.busyAction === action;
        },

        async runTransition(action, button, transition, successMessage) {
            if (this.busyAction !== null) {
                return false;
            }

            const plan = focusPlan(button);
            this.busyAction = action;
            this.errorMessage = '';
            this.statusMessage = '';

            try {
                const outcome = await transition();
                // A server that refused the change on purpose resolves to exactly false: its own visible notice
                // explains why, so no success is announced and no generic error is raised.
                if (outcome !== false) {
                    this.statusMessage = String(successMessage);
                }
                await afterRender();
                focusFromPlan(plan);

                return outcome !== false;
            } catch {
                this.errorMessage =
                    'No se pudo actualizar la preparación. Inténtalo de nuevo.';
                await afterRender();
                let focusedTarget;
                if (canReceiveFocus(button) && button.isConnected !== false) {
                    button.focus();
                    focusedTarget = button;
                } else {
                    focusedTarget = focusFromPlan(plan);
                }
                await afterRender();
                if (
                    'activeElement' in documentTarget &&
                    (documentTarget.activeElement !== focusedTarget ||
                        focusedTarget?.isConnected === false)
                ) {
                    focusFromPlan(plan);
                }

                return false;
            } finally {
                this.busyAction = null;
            }
        },
    };
}

function installOperationalAccessibility({
    documentTarget = document,
    windowTarget = window,
} = {}) {
    windowTarget[DIALOG_INSTALL_KEY]?.dispose();

    let confirmStore = null;
    let toastStore = null;
    let disposed = false;
    let livewireHookCleanup = null;
    let livewireHookOwner = null;

    const accessibleDialogFactory = (options) =>
        createAccessibleDialog(options);
    const kdsAccessibilityFactory = () =>
        createKdsAccessibility({ documentTarget });
    windowTarget.createAccessibleDialog = accessibleDialogFactory;
    windowTarget.createKdsAccessibility = kdsAccessibilityFactory;

    function registerStores() {
        if (disposed || !windowTarget.Alpine) {
            return;
        }

        toastStore?.dispose();
        toastStore = createToastStore({
            clearTimeoutTarget: windowTarget.clearTimeout.bind(windowTarget),
            documentTarget,
            fallbackFocus: () => {
                const remembered = lastFocusedElements.get(documentTarget);
                const replacement = remembered?.id
                    ? documentTarget.getElementById?.(remembered.id)
                    : null;

                return [
                    remembered?.element,
                    replacement,
                    documentTarget.querySelector?.(
                        '[data-operational-focus-fallback]',
                    ),
                ];
            },
            setTimeoutTarget: windowTarget.setTimeout.bind(windowTarget),
        });
        confirmStore = createConfirmStore({
            onError: (message) => toastStore.push({ message, type: 'error' }),
        });
        windowTarget.Alpine.store('toasts', toastStore);
        windowTarget.Alpine.store('confirm', confirmStore);
    }

    function rememberFocus(event) {
        const currentDialog = activeDialog(documentTarget);
        if (
            currentDialog?.contains(event.target) ||
            event.target?.closest?.('[data-toast-close]') ||
            event.target === documentTarget.body ||
            event.target === documentTarget.documentElement
        ) {
            return;
        }

        lastFocusedElements.set(documentTarget, {
            element: event.target,
            id: event.target?.id || null,
        });
    }

    function attachLivewireHook() {
        const livewire = windowTarget.Livewire;
        if (!livewire || livewireHookOwner === livewire) {
            return;
        }

        livewireHookCleanup?.();
        livewireHookCleanup = null;
        livewireHookOwner = livewire;
        if (typeof livewire.hook === 'function') {
            livewireHookCleanup = livewire.hook('morphed', () => {
                releaseDisconnectedDialog(documentTarget);
                resumeFocusRestoration(documentTarget);
            });
        }
    }

    function handleLivewireReady() {
        attachLivewireHook();
        releaseDisconnectedDialog(documentTarget);
        resumeFocusRestoration(documentTarget);
    }

    function handleLivewireNavigation() {
        deactivateDialogs(documentTarget);
        pendingFocusRestorations.delete(documentTarget);
        lastFocusedElements.delete(documentTarget);
        confirmStore?.close(true);
        toastStore?.dispose();
    }

    documentTarget.addEventListener('alpine:init', registerStores);
    documentTarget.addEventListener('focusin', rememberFocus, true);
    documentTarget.addEventListener(
        'livewire:initialized',
        handleLivewireReady,
    );
    documentTarget.addEventListener('livewire:navigated', handleLivewireReady);
    documentTarget.addEventListener(
        'livewire:navigating',
        handleLivewireNavigation,
    );
    if (windowTarget.Alpine) {
        registerStores();
    }
    attachLivewireHook();

    const installation = {
        dispose() {
            if (disposed) {
                return;
            }

            disposed = true;
            documentTarget.removeEventListener('alpine:init', registerStores);
            documentTarget.removeEventListener('focusin', rememberFocus, true);
            documentTarget.removeEventListener(
                'livewire:initialized',
                handleLivewireReady,
            );
            documentTarget.removeEventListener(
                'livewire:navigated',
                handleLivewireReady,
            );
            documentTarget.removeEventListener(
                'livewire:navigating',
                handleLivewireNavigation,
            );
            livewireHookCleanup?.();
            livewireHookCleanup = null;
            livewireHookOwner = null;
            deactivateDialogs(documentTarget);
            pendingFocusRestorations.delete(documentTarget);
            lastFocusedElements.delete(documentTarget);
            toastStore?.dispose();
            confirmStore?.close(true);

            if (
                windowTarget.createAccessibleDialog === accessibleDialogFactory
            ) {
                delete windowTarget.createAccessibleDialog;
            }

            if (
                windowTarget.createKdsAccessibility === kdsAccessibilityFactory
            ) {
                delete windowTarget.createKdsAccessibility;
            }

            if (windowTarget[DIALOG_INSTALL_KEY] === installation) {
                delete windowTarget[DIALOG_INSTALL_KEY];
            }
        },
    };
    windowTarget[DIALOG_INSTALL_KEY] = installation;

    return installation;
}

export {
    createAccessibleDialog,
    createConfirmStore,
    createDialogLifecycle,
    createKdsAccessibility,
    createToastStore,
    installOperationalAccessibility,
    releaseDisconnectedDialog,
};
